import React, { useEffect, useRef, useState } from 'react'
import { Loader2 } from 'lucide-react'
import { api } from '../../../lib/api'
import { Button } from '../../../components/ui/button'
import { Input } from '../../../components/ui/input'
import { Label } from '../../../components/ui/label'
import { givingModeText } from '../parish/GivingSettings'

// Mirrors VERIFICATION_TRANSITIONS in backend/src/nonprofit/org-verification.service.ts
const VERIFICATION_TRANSITIONS = {
  unverified: ['pending'],
  pending: ['verified', 'rejected', 'unverified'],
  verified: ['pending', 'rejected'],
  rejected: ['pending'],
}

const STATUS_LABELS = {
  unverified: 'Not yet submitted',
  pending: 'Pending review',
  verified: 'Verified',
  rejected: 'Rejected',
}

const DOCUMENT_KINDS = [
  { value: 'irs_ein_letter', label: 'IRS EIN letter' },
  { value: 'diocesan_inclusion_letter', label: 'Diocesan inclusion letter' },
  { value: 'other', label: 'Other' },
]

const kindLabel = (kind) => DOCUMENT_KINDS.find((k) => k.value === kind)?.label || kind

const formatDate = (iso) => (iso ? new Date(iso).toLocaleString() : '')

export default function VerificationPanel({ parishId, refreshKey = 0 }) {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState('')
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)
  const [orgType, setOrgType] = useState('parish')
  const [legalName, setLegalName] = useState('')
  const [ein, setEin] = useState('')
  const [notes, setNotes] = useState('')
  const [docKind, setDocKind] = useState('irs_ein_letter')
  const lastParishId = useRef(null)

  // Only "Save details" and the first load reset the editable fields, so other
  // actions (status, documents, Candid) and refreshes keep unsaved edits.
  const applyView = (view, resetFields = false) => {
    setData(view)
    if (!resetFields) return
    setOrgType(view.orgType || 'parish')
    setLegalName(view.legalName || '')
    setEin(view.ein || '')
    setNotes(view.verificationNotes || '')
  }

  useEffect(() => {
    let cancelled = false
    const initial = refreshKey === 0 || lastParishId.current !== parishId
    lastParishId.current = parishId
    if (initial) {
      setLoading(true)
      setLoadError('')
    }
    api
      .getOrgVerification(parishId)
      .then((view) => {
        if (!cancelled) applyView(view, initial)
      })
      .catch((err) => {
        if (!cancelled) setLoadError(err.message || 'Failed to load verification')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [parishId, refreshKey])

  // Runs an action that resolves to a fresh verification view; shows the
  // server's message inline when it rejects (e.g. missing prerequisites).
  const run = async (action, resetFields = false) => {
    setError('')
    setBusy(true)
    try {
      const view = await action()
      if (view) applyView(view, resetFields)
    } catch (err) {
      setError(err.message || 'Request failed')
    } finally {
      setBusy(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-6 h-6 text-gold animate-spin" />
      </div>
    )
  }
  if (loadError) return <p role="alert" className="text-sm text-red-600">{loadError}</p>
  if (!data) return null

  const status = data.verificationStatus
  const transitions = VERIFICATION_TRANSITIONS[status] || []
  const documents = data.verificationDocuments || []

  const handleSaveDetails = (e) => {
    e.preventDefault()
    run(() =>
      api.updateOrgVerification(parishId, {
        orgType,
        legalName: legalName.trim() || null,
        ein: ein.trim() || null,
        verificationNotes: notes.trim() || null,
      }),
      true,
    )
  }

  const handleDownload = async (path) => {
    setError('')
    // Open the tab synchronously inside the click: Safari blocks window.open
    // once we have awaited. Point it at the signed URL when it resolves.
    const win = window.open('', '_blank')
    if (win) win.opener = null
    try {
      const res = await api.getVerificationDocumentUrl(parishId, path)
      if (win) win.location.href = res.url
      else window.open(res.url, '_blank', 'noopener')
    } catch (err) {
      if (win) win.close()
      setError(err.message || 'Failed to open document')
    }
  }

  const handleUpload = (e) => {
    const file = e.target.files && e.target.files[0]
    e.target.value = ''
    if (!file) return
    run(() => api.uploadVerificationDocument(parishId, file, docKind))
  }

  const selectClass =
    'mt-1 w-full px-3 py-2 bg-white border border-surface-border rounded text-charcoal focus:outline-none focus:ring-2 focus:ring-gold/50'

  return (
    <div className="space-y-6">
      <form onSubmit={handleSaveDetails} noValidate className="space-y-4 max-w-xl">
        <div>
          <Label htmlFor="verification-org-type" className="text-charcoal text-xs">Organization type</Label>
          <select
            id="verification-org-type"
            value={orgType}
            onChange={(e) => setOrgType(e.target.value)}
            className={selectClass}
          >
            <option value="parish">Parish</option>
            <option value="catholic_center">Catholic center</option>
          </select>
        </div>
        <div>
          <Label htmlFor="verification-legal-name" className="text-charcoal text-xs">Legal name</Label>
          <Input
            id="verification-legal-name"
            value={legalName}
            maxLength={200}
            onChange={(e) => setLegalName(e.target.value)}
            className="mt-1 bg-white border-surface-border text-charcoal"
          />
        </div>
        <div>
          <Label htmlFor="verification-ein" className="text-charcoal text-xs">EIN</Label>
          <Input
            id="verification-ein"
            value={ein}
            placeholder="12-3456789"
            onChange={(e) => setEin(e.target.value)}
            className="mt-1 bg-white border-surface-border text-charcoal"
          />
        </div>
        <div>
          <Label htmlFor="verification-notes" className="text-charcoal text-xs">Notes</Label>
          <textarea
            id="verification-notes"
            value={notes}
            rows={3}
            maxLength={5000}
            onChange={(e) => setNotes(e.target.value)}
            className="mt-1 w-full px-3 py-2 bg-white border border-surface-border rounded text-charcoal focus:outline-none focus:ring-2 focus:ring-gold/50"
          />
        </div>
        <div className="flex justify-end">
          <Button type="submit" disabled={busy} className="bg-gold text-black hover:bg-gold/90">
            Save details
          </Button>
        </div>
      </form>

      <div className="space-y-3">
        <div className="flex items-center gap-3 text-sm">
          <span className="text-charcoal-muted">Status</span>
          <span className="px-2 py-0.5 rounded border border-surface-border text-charcoal font-medium">
            {STATUS_LABELS[status] || status}
          </span>
        </div>
        <div className="flex flex-wrap gap-2">
          {transitions.map((to) => (
            <Button
              key={to}
              type="button"
              variant="outline"
              disabled={busy}
              onClick={() => run(() => api.updateOrgVerification(parishId, { verificationStatus: to }))}
            >
              {`Mark ${to}`}
            </Button>
          ))}
        </div>
        {data.verifiedAt && (
          <p className="text-sm text-charcoal-muted">
            Verified {formatDate(data.verifiedAt)}
            {data.verifiedBy?.email ? ` by ${data.verifiedBy.email}` : ''}
          </p>
        )}
      </div>

      <div className="space-y-2">
        <h3 className="text-sm font-semibold text-charcoal">Documents</h3>
        {documents.length === 0 ? (
          <p className="text-sm text-charcoal-muted">No documents uploaded.</p>
        ) : (
          <ul className="divide-y divide-surface-border border border-surface-border rounded">
            {documents.map((doc) => (
              <li key={doc.path} className="flex items-center justify-between gap-3 px-3 py-2 text-sm">
                <span className="text-charcoal">
                  {kindLabel(doc.kind)}
                  <span className="text-charcoal-muted"> · {formatDate(doc.uploadedAt)}</span>
                </span>
                <span className="flex gap-2">
                  <Button type="button" variant="outline" size="sm" onClick={() => handleDownload(doc.path)}>
                    Download
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={busy}
                    onClick={() => run(() => api.deleteVerificationDocument(parishId, doc.path))}
                  >
                    Remove
                  </Button>
                </span>
              </li>
            ))}
          </ul>
        )}
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <Label htmlFor="verification-doc-kind" className="text-charcoal text-xs">Document kind</Label>
            <select
              id="verification-doc-kind"
              value={docKind}
              onChange={(e) => setDocKind(e.target.value)}
              className={selectClass}
            >
              {DOCUMENT_KINDS.map((k) => (
                <option key={k.value} value={k.value}>{k.label}</option>
              ))}
            </select>
          </div>
          <div>
            <Label htmlFor="verification-doc-file" className="text-charcoal text-xs">Document file</Label>
            <input
              id="verification-doc-file"
              type="file"
              accept=".pdf,.jpg,.jpeg,.png"
              disabled={busy}
              onChange={handleUpload}
              className="mt-1 block text-sm text-charcoal"
            />
          </div>
        </div>
      </div>

      <div className="space-y-1">
        <label className="flex items-center gap-2 text-sm text-charcoal">
          <input
            type="checkbox"
            checked={!!data.candidEligible}
            disabled={data.candidApiConfigured !== false || busy}
            onChange={(e) => run(() => api.updateOrgVerification(parishId, { candidEligible: e.target.checked }))}
          />
          Candid seal confirmed
        </label>
        {data.candidApiConfigured !== false && (
          <p className="text-xs text-charcoal-muted">
            Checked daily by Candid.
            {data.candidCheckedAt ? ` Last checked ${formatDate(data.candidCheckedAt)}` : ''}
          </p>
        )}
        {data.candidLastError && <p className="text-xs text-red-600">{data.candidLastError}</p>}
      </div>

      <p className="text-sm text-charcoal">
        <span className="text-charcoal-muted">Giving mode: </span>
        {givingModeText(data.giving) || 'Unknown'}
      </p>

      {error && <p role="alert" className="text-sm text-red-600">{error}</p>}
    </div>
  )
}
